import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';

import { apiErrorMessage } from '../../shared/api/errors';
import { formatDate } from '../../shared/lib/format';
import { formatPhoneRu, phoneComplete, phoneDigits } from '../../shared/lib/phone';
import { Button } from '../../shared/ui/Button';
import { TextField, inputClass } from '../../shared/ui/TextField';
import { Card } from '../../shared/ui/primitives';
import { checkPatientDuplicates, createPatient, getPatient, updatePatient } from './api';
import type { PatientInput } from './types';

const schema = z.object({
  lastName: z.string().min(1, 'Укажите фамилию'),
  firstName: z.string().min(1, 'Укажите имя'),
  middleName: z.string().optional(),
  birthDate: z
    .string()
    .optional()
    .refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), 'Дата в формате ГГГГ-ММ-ДД'),
  phone: z.string().optional(),
  email: z.string().email('Некорректный e-mail').optional().or(z.literal('')),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export function PatientFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ['patient', id],
    queryFn: () => getPatient(id as string),
    enabled: isEdit,
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  useEffect(() => {
    if (existing) {
      reset({
        lastName: existing.lastName,
        firstName: existing.firstName,
        middleName: existing.middleName,
        birthDate: existing.birthDate ?? '',
        phone: existing.phone,
        email: existing.email,
        notes: existing.notes,
      });
    }
  }, [existing, reset]);

  const phone = watch('phone') ?? '';
  const digits = phoneDigits(phone);
  const dupQuery = useQuery({
    queryKey: ['dupcheck', digits],
    queryFn: () => checkPatientDuplicates(phone),
    enabled: phoneComplete(phone),
    staleTime: 30_000,
  });
  const duplicates = (dupQuery.data ?? []).filter((m) => m.id !== id);

  const mutation = useMutation({
    mutationFn: (body: PatientInput) =>
      isEdit ? updatePatient(id as string, body) : createPatient(body),
    onSuccess: async (patient) => {
      await queryClient.invalidateQueries({ queryKey: ['patients'] });
      await queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
      navigate(`/patients/${patient.id}`, { replace: true });
    },
  });

  const onSubmit = handleSubmit((values) => {
    const parsed = schema.safeParse(values);
    if (!parsed.success) return;
    mutation.mutate(parsed.data);
  });

  return (
    <section className="mx-auto max-w-2xl space-y-5">
      <Link
        to={isEdit ? `/patients/${id}` : '/patients'}
        className="text-sm font-medium text-accent hover:text-accent-600"
      >
        ← Назад
      </Link>
      <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">
        {isEdit ? 'Редактирование карточки' : 'Новая карточка пациента'}
      </h1>

      <Card className="p-5 sm:p-6">
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Фамилия *" error={errors.lastName?.message} {...register('lastName')} />
            <TextField label="Имя *" error={errors.firstName?.message} {...register('firstName')} />
            <TextField label="Отчество" {...register('middleName')} />
            <TextField
              label="Дата рождения"
              type="date"
              error={errors.birthDate?.message}
              {...register('birthDate')}
            />
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-ink/80">Телефон</span>
              <Controller
                name="phone"
                control={control}
                render={({ field: f }) => (
                  <input
                    className={inputClass}
                    inputMode="tel"
                    placeholder="+7(___)___-__-__"
                    value={f.value ?? ''}
                    onChange={(e) => f.onChange(formatPhoneRu(e.target.value))}
                    onBlur={f.onBlur}
                  />
                )}
              />
            </label>
            <TextField label="E-mail" type="email" error={errors.email?.message} {...register('email')} />
          </div>

          {duplicates.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <p className="font-semibold">Возможно, такой пациент уже есть (совпал телефон):</p>
              <ul className="mt-1 space-y-1">
                {duplicates.map((m, i) => (
                  <li key={m.id ?? i}>
                    {m.id ? (
                      <Link to={`/patients/${m.id}`} className="font-medium underline">
                        {m.fullName}
                      </Link>
                    ) : (
                      <span className="font-medium">{m.fullName}</span>
                    )}
                    {m.birthDate && <span className="text-amber-700"> · {formatDate(m.birthDate)}</span>}
                    {m.doctors.length > 0 && (
                      <span className="text-amber-700"> · врачи: {m.doctors.join(', ')}</span>
                    )}
                    {!m.mine && <span className="text-amber-700"> · у другого врача</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-ink/80">Заметки</span>
            <textarea rows={4} className={inputClass} {...register('notes')} />
          </label>

          {mutation.isError && (
            <p className="text-sm text-danger">{apiErrorMessage(mutation.error, 'Не удалось сохранить')}</p>
          )}

          <Button type="submit" disabled={mutation.isPending}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </form>
      </Card>
    </section>
  );
}
